import { useState } from "react";
import { Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    description: "",
    website_url: "", // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle, submitting, success, error
  const [errorMessage, setErrorMessage] = useState("");

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Navn er påkrævet";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Telefonnummer er påkrævet";
    } else if (!/^[0-9\s+]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = "Ugyldigt telefonnummer (skal være mindst 8 tal)";
    }
    if (!formData.email.trim()) {
      newErrors.email = "E-mail er påkrævet";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = "Ugyldig e-mailadresse";
    }
    if (!formData.description.trim()) {
      newErrors.description = "Beskrivelse af opgaven er påkrævet";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus("submitting");
    setErrorMessage("");

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus("success");
        setFormData({
          name: "",
          phone: "",
          email: "",
          description: "",
          website_url: "",
        });
      } else {
        throw new Error("Der skete en fejl ved afsendelsen. Prøv venligst igen.");
      }
    } catch (err) {
      setStatus("error");
      setErrorMessage(err.message || "Kunne ikke oprette forbindelse til serveren.");
    }
  };

  if (status === "success") {
    return (
      <div className="bg-emerald-950/40 border border-emerald-800/60 p-8 text-center rounded-none animate-fadeIn">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-none bg-emerald-900/50 text-emerald-400 mb-4">
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <h3 className="text-lg font-bold uppercase tracking-wider text-stone-100 mb-2">
          Tak for din henvendelse!
        </h3>
        <p className="text-sm text-stone-300 max-w-md mx-auto leading-relaxed">
          Vi har modtaget dine oplysninger og vil vende tilbage til dig med et uforpligtende tilbud eller for at aftale et møde inden for 24 timer.
        </p>
        <button
          onClick={() => setStatus("idle")}
          className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 bg-amber-800 hover:bg-amber-700 text-stone-50 text-xs font-bold uppercase tracking-widest transition-colors duration-200 rounded-none cursor-pointer"
        >
          Send en ny besked
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 rounded-none">
      {/* Honeypot field - absolutely hidden from humans */}
      <div className="sr-only absolute -top-9999 -left-9999" aria-hidden="true">
        <label htmlFor="website_url">Udfyld ikke dette felt, hvis du er et menneske</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          value={formData.website_url}
          onChange={handleChange}
          tabIndex="-1"
          autoComplete="off"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {/* Name */}
        <div className="space-y-1.5">
          <label htmlFor="name" className="block text-xs font-bold uppercase tracking-widest text-stone-300">
            Navn <span className="text-amber-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Dit fulde navn"
            className={`w-full px-4 py-3 bg-zinc-900/80 border text-stone-100 text-sm rounded-none focus:outline-none focus:border-amber-500 transition-colors duration-200 ${
              errors.name ? "border-red-500" : "border-zinc-800"
            }`}
          />
          {errors.name && <p className="text-xs text-red-500 font-medium">{errors.name}</p>}
        </div>

        {/* Phone */}
        <div className="space-y-1.5">
          <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-widest text-stone-300">
            Telefonnummer <span className="text-amber-500">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="F.eks. 20949740"
            className={`w-full px-4 py-3 bg-zinc-900/80 border text-stone-100 text-sm rounded-none focus:outline-none focus:border-amber-500 transition-colors duration-200 ${
              errors.phone ? "border-red-500" : "border-zinc-800"
            }`}
          />
          {errors.phone && <p className="text-xs text-red-500 font-medium">{errors.phone}</p>}
        </div>
      </div>

      {/* Email */}
      <div className="space-y-1.5">
        <label htmlFor="email" className="block text-xs font-bold uppercase tracking-widest text-stone-300">
          E-mailadresse <span className="text-amber-500">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="kontakt@nsts.dk"
          className={`w-full px-4 py-3 bg-zinc-900/80 border text-stone-100 text-sm rounded-none focus:outline-none focus:border-amber-500 transition-colors duration-200 ${
            errors.email ? "border-red-500" : "border-zinc-800"
          }`}
        />
        {errors.email && <p className="text-xs text-red-500 font-medium">{errors.email}</p>}
      </div>

      {/* Description */}
      <div className="space-y-1.5">
        <label htmlFor="description" className="block text-xs font-bold uppercase tracking-widest text-stone-300">
          Beskrivelse af opgaven <span className="text-amber-500">*</span>
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows="5"
          placeholder="Beskriv dit projekt (f.eks. størrelse på terrasse, tagtype, tidsramme osv.)"
          className={`w-full px-4 py-3 bg-zinc-900/80 border text-stone-100 text-sm rounded-none focus:outline-none focus:border-amber-500 transition-colors duration-200 resize-none ${
            errors.description ? "border-red-500" : "border-zinc-800"
          }`}
        />
        {errors.description && <p className="text-xs text-red-500 font-medium">{errors.description}</p>}
      </div>

      {/* Error Alert */}
      {status === "error" && (
        <div className="flex items-start gap-3 bg-red-950/40 border border-red-900/50 p-4 rounded-none text-red-400 text-xs">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <p className="leading-relaxed">{errorMessage}</p>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={status === "submitting"}
        className="w-full sm:w-auto px-8 py-4 bg-amber-800 hover:bg-amber-700 disabled:bg-zinc-800 text-stone-50 text-xs font-bold uppercase tracking-widest transition-all duration-200 rounded-none flex items-center justify-center gap-2 cursor-pointer"
      >
        {status === "submitting" ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Sender...</span>
          </>
        ) : (
          <>
            <Send className="w-4 h-4" />
            <span>Send forespørgsel</span>
          </>
        )}
      </button>
    </form>
  );
}
